import * as React from "react";
import {
  Button,
  Stack,
  Radio,
  RadioGroup,
  Heading,
  Alert,
  TextLink,
  Text,
} from "@origin-digital/ods-core";
import FormFieldLayout from "../FormComponents/FormFieldLayout";
import RequestingCompany from "../FormComponents/RequestingCompany";
import {
  FieldProps,
  FormikProps,
  Field,
  useFormikContext,
  yupToFormErrors,
} from "formik";
import { useState } from "react";
import ReadOnlyTextField from "../FormComponents/ReadOnlyTextField";
import { useAppContext } from "src/context/AppContext";
import { ChangeRequestFormValues } from "./formValue";
import Nmi from "./Nmi";
import StatusAlert, { NmiStatusEnum } from "./StatusAlert";
import { changeRequestPremiseValidationSchema } from "./validationSchema";
import { apiClient } from "src/api/apiService";
import ChangeRequestContext from "../../../context/ChangeRequestContext";
import { ErrorTypeEnum } from "../types";
import { getFormattedAddress } from "src/components/Premises/helpers";
import { PremiseDetails } from "src/components/Premises/types";
import {
  energyTypeOptions,
  mapFormikValuesToDraftService,
  mapRequestingCompanyToResponse,
  relationshipOptions,
} from "./helpers";
import { PermissionEnum } from "src/components/Permissions/types";
import PermissionsControlledWrapper from "src/components/Permissions/PermissionsControlledWrapper";

const ChangeRequestPremiseBlock: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const {
    setDisplayingSnackbar,
    setPremiseAddress,
    setDisplayingAlertBanner,
    setActiveSection,
    setDraftInfo,
  } = React.useContext(ChangeRequestContext);
  const {
    context: { user },
  } = useAppContext();

  const { values, errors, setFieldValue, submitForm, validateForm, setErrors } =
    useFormikContext<ChangeRequestFormValues>();
  const { noCompany } = values;

  React.useEffect(() => {
    if (Object.keys(errors).length > 0) {
      //Check all the fields within the whole form. If any field has an error, display the error banner.
      setDisplayingAlertBanner(ErrorTypeEnum.OCP_FIELD_VALIDATION);
    } else {
      setDisplayingAlertBanner(null);
    }
  }, [errors]);

  const { nmiStatus, energyType, premise } = values;

  const onClickCreatePremise = () => {
    changeRequestPremiseValidationSchema
      .validate(values, {
        abortEarly: false,
        context: {
          noCompany,
        },
      })
      .then(async () => {
        setIsLoading(true);
        const updatedPremiseWithRequestingCompany = {
          ...values.premise,
          site_details: {
            ...values.premise?.site_details,
            requesting_company: values.requestingCompany
              ? mapRequestingCompanyToResponse(values.requestingCompany)
              : null,
          },
        };
        const { data: draftData } = await apiClient.post("/draft/premise", {
          premise: updatedPremiseWithRequestingCompany,
          services: [
            {
              service_type: "ELECTRICITY",
              request_type: "CHANGE_SUPPLY",
              payload: mapFormikValuesToDraftService(values),
              service_identifier: values.nmi,
            },
          ],
        });
        setDraftInfo({
          draft_id: draftData.services[0].draft_id,
          draft_version: draftData.services[0].draft_version,
          request_id: draftData.services[0].request_id,
          raw_request_status: draftData.services[0].raw_request_status,
          request_type: draftData.services[0].request_type,
          message_enabled: draftData.services[0].message_enabled,
        });
        setFieldValue("noCompany", noCompany);
        setFieldValue("premise", draftData.premise);
        setPremiseAddress(
          premise ? getFormattedAddress(premise as PremiseDetails) : null
        );
        setDisplayingSnackbar({
          message: `Premises has been submitted.`,
          tone: "positive",
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
        setActiveSection("premiseReadonlySection");
      })
      .catch((e) => {
        setErrors(yupToFormErrors(e));
        setDisplayingSnackbar({
          message: `Couldn't create premises`,
          tone: "caution",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const isDisabled =
    energyType === "naturalGas" ||
    energyType === "lpg" ||
    (nmiStatus !== null && nmiStatus !== NmiStatusEnum.ELIGIBLE);

  return (
    <Stack
      space={{
        xs: "xlarge",
        md: "xxlarge",
      }}
      id="premiseSection"
    >
      <Heading variant="h4">Premises</Heading>

      {noCompany && (
        <FormFieldLayout>
          <ReadOnlyTextField
            label="Requestor name"
            value={
              values.requesterName || `${user?.first_name} ${user?.last_name}`
            }
          />
        </FormFieldLayout>
      )}

      {noCompany && (
        <FormFieldLayout>
          <ReadOnlyTextField
            label="Requestor email"
            value={values.requesterEmail || user?.email || ""}
          />
        </FormFieldLayout>
      )}

      {!noCompany && (
        <FormFieldLayout>
          <RequestingCompany
            error={errors["requestingCompany"]}
            setFieldValue={setFieldValue}
            submitForm={submitForm}
            validateForm={validateForm}
            isEditing={false}
            requestingCompany={values.requestingCompany}
            fullWidth
            setNoCompany={() => setFieldValue("noCompany", true)}
          />
        </FormFieldLayout>
      )}

      <FormFieldLayout>
        <Field name="relationship">
          {({ field }: { field: FormikProps<FieldProps> }) => (
            <RadioGroup
              {...field}
              id="relationship"
              name="relationship"
              label="What’s your relationship to the property?"
            >
              {relationshipOptions.map(({ id, label, value }) => (
                <Radio key={id} id={id} label={label} value={value} />
              ))}
            </RadioGroup>
          )}
        </Field>
      </FormFieldLayout>

      <FormFieldLayout multipleFields>
        <Field name="energyType">
          {({ field }: { field: FormikProps<FieldProps> }) => (
            <RadioGroup
              {...field}
              id="energyType"
              name="energyType"
              label="Which energy type?"
              defaultValue="electricity"
            >
              {energyTypeOptions.map(({ id, label, value }) => (
                <Radio key={id} id={id} label={label} value={value} />
              ))}
            </RadioGroup>
          )}
        </Field>
        {energyType === "naturalGas" && (
          <Alert title="Sorry you can’t use this form to make changes for Natural gas">
            <Text>
              To make changes to your Natural gas supply, please call 132 461.
            </Text>
          </Alert>
        )}
        {energyType === "lpg" && (
          <Alert title="Sorry you can’t use this form to make changes for LPG">
            <Text>
              To make changes to your LPG supply,{" "}
              <TextLink
                href="https://www.originenergy.com.au/lpg/"
                target="_blank"
              >
                visit Origin LPG
              </TextLink>
            </Text>
          </Alert>
        )}
      </FormFieldLayout>

      <FormFieldLayout text="The National Metering Identifier (NMI) can be found on the latest electricity bill. ">
        <Nmi requestType="CHANGE_SUPPLY" />
      </FormFieldLayout>
      {nmiStatus || premise ? (
        <FormFieldLayout multipleFields>
          {nmiStatus && (
            <StatusAlert
              status={nmiStatus}
              type="nmi"
              requestType="CHANGE_SUPPLY"
            />
          )}
          {premise && (
            <ReadOnlyTextField
              label="Address"
              value={getFormattedAddress(premise)}
            />
          )}
        </FormFieldLayout>
      ) : null}

      <PermissionsControlledWrapper
        requiredPermission={PermissionEnum.CREATE_REQUEST}
      >
        <Button
          onClick={onClickCreatePremise}
          disabled={isDisabled}
          spinner={isLoading}
        >
          Create premises
        </Button>
      </PermissionsControlledWrapper>
    </Stack>
  );
};

export default ChangeRequestPremiseBlock;
